// src/screens/settings/pages/CategoryModelsPage.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  GripVertical,
  ChevronDown,
  ChevronUp,
  X,
  FileText,
} from "lucide-react";
import { createHttpClient } from "../../../api/client";
import { ReactComponent as PlusCircle } from "../../../assets/images/plus.svg";
import { ReactComponent as Pen } from "../../../assets/images/Pen.svg";
import { ReactComponent as Trash2 } from "../../../assets/images/Trash.svg";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function ActionButton({ onClick, children, danger = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "h-10 w-10 rounded-xl inline-flex items-center justify-center transition",
        "bg-[#F5F5F5]",
        danger ? "text-[#FF1607] hover:bg-[#FFF3F3]" : "text-[#1D1616] hover:bg-[#ECECEC]",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      {children}
    </button>
  );
}

function InlineCreateRow({
  placeholder,
  loading,
  onSubmit,
}) {
  const [value, setValue] = React.useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;

    await onSubmit?.({
      value: value.trim(),
    });

    setValue("");
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-3">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="h-16 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none md:col-span-8"
      />

      <button
        type="submit"
        disabled={loading || !value.trim()}
        className={cx(
          "h-16 rounded-xl text-sm font-bold text-white md:col-span-4",
          loading || !value.trim()
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-[#111111] hover:opacity-95 active:scale-[0.99]"
        )}
      >
        {loading ? "กำลังเพิ่ม..." : "เพิ่มรายการ"}
      </button>
    </form>
  );
}

function EditModal({
  open,
  title = "แก้ไขรายการ",
  label = "ชื่อรายการ",
  value = "",
  confirmText = "บันทึก",
  cancelText = "ยกเลิก",
  loading = false,
  onClose,
  onConfirm,
}) {
  const [text, setText] = React.useState(value || "");

  React.useEffect(() => {
    if (!open) return;
    setText(value || "");
  }, [open, value]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90]">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-lg font-extrabold text-slate-900 truncate">
                {title}
              </div>
            </div>

            <button
              type="button"
              onClick={loading ? undefined : onClose}
              className="h-9 w-9 rounded-xl border border-slate-200 bg-white inline-flex items-center justify-center hover:bg-slate-50 active:scale-[0.98]"
              aria-label="close"
              title="ปิด"
              disabled={loading}
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-5 py-5">
            <div className="text-sm font-semibold text-slate-700 mb-2">
              {label}
            </div>
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-slate-400"
              placeholder="กรอกข้อมูล"
              disabled={loading}
            />
          </div>

          <div className="px-5 pb-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-xl px-4 py-2 text-sm font-bold border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.99] disabled:opacity-60"
            >
              {cancelText}
            </button>

            <button
              type="button"
              onClick={() => onConfirm?.(text)}
              disabled={loading || !String(text).trim()}
              className="rounded-xl px-4 py-2 text-sm font-bold text-white active:scale-[0.99] bg-[#111111] hover:opacity-95 disabled:opacity-60"
            >
              {loading ? "กำลังบันทึก..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateModal({
  open,
  title = "เทมเพลตคำอธิบายสินค้า",
  value = "",
  loading = false,
  onClose,
  onSave,
}) {
  const [text, setText] = React.useState(value || "");

  React.useEffect(() => {
    if (!open) return;
    setText(value || "");
  }, [open, value]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[95]">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={loading ? undefined : onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="text-2xl font-extrabold text-slate-900">
              {title}
            </div>

            <button
              type="button"
              onClick={loading ? undefined : onClose}
              className="h-10 w-10 rounded-xl bg-white inline-flex items-center justify-center hover:bg-slate-50"
              disabled={loading}
            >
              <X size={22} />
            </button>
          </div>

          <div className="px-8 py-6">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={18}
              disabled={loading}
              className="w-full rounded-xl border border-slate-300 bg-white p-4 text-lg leading-8 outline-none resize-none"
              placeholder="กรอกเทมเพลต..."
            />
          </div>

          <div className="px-8 pb-8 grid grid-cols-2 gap-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-14 rounded-2xl border border-slate-300 bg-white text-xl font-bold hover:bg-slate-50 disabled:opacity-60"
            >
              ยกเลิก
            </button>

            <button
              type="button"
              onClick={() => onSave?.(text)}
              disabled={loading}
              className="h-14 rounded-2xl bg-[#111111] text-white text-xl font-bold hover:opacity-95 disabled:opacity-60"
            >
              {loading ? "กำลังบันทึก..." : "บันทึก"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function useResource({ endpoint, paramsBuilder, createPayloadBuilder }) {
  const http = React.useMemo(() => createHttpClient(), []);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState(null);

  const fetchItems = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await http.get(endpoint, { params: paramsBuilder() });
      setItems(res?.data?.data || []);
    } catch (err) {
      console.error(`GET ${endpoint} error:`, err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [http, endpoint, paramsBuilder]);

  const createItem = React.useCallback(
    async (payloadInput) => {
      try {
        setCreating(true);
        await http.post(endpoint, {
          data: createPayloadBuilder(payloadInput, items),
        });
        await fetchItems();
      } catch (err) {
        console.error(`POST ${endpoint} error:`, err);
        alert("เพิ่มรายการไม่สำเร็จ");
      } finally {
        setCreating(false);
      }
    },
    [http, endpoint, createPayloadBuilder, items, fetchItems]
  );

  const deleteItem = React.useCallback(
    async (item) => {
      const ok = window.confirm(`ต้องการลบ "${item?.name || item?.size || "-"}" ใช่หรือไม่?`);
      if (!ok) return;

      try {
        setDeletingId(item.id);
        await http.delete(`${endpoint}/${item.id}`);
        await fetchItems();
      } catch (err) {
        console.error(`DELETE ${endpoint}/${item.id} error:`, err);
        alert("ลบรายการไม่สำเร็จ");
      } finally {
        setDeletingId(null);
      }
    },
    [http, endpoint, fetchItems]
  );

  return {
    items,
    loading,
    creating,
    deletingId,
    fetchItems,
    createItem,
    deleteItem,
    http,
  };
}

function ModelChildren({ modelId }) {
  const colors = useResource({
    endpoint: "/colors",
    paramsBuilder: () => ({
      filters: { model: { id: { $eq: modelId } } },
      sort: ["sequence:asc", "id:asc"],
      pagination: { pageSize: 200 },
    }),
    createPayloadBuilder: ({ value }, items) => ({
      name: value,
      sequence: items.length,
      model: modelId,
    }),
  });

  const capacities = useResource({
    endpoint: "/capacities",
    paramsBuilder: () => ({
      filters: { model: { id: { $eq: modelId } } },
      sort: ["sequence:asc", "id:asc"],
      pagination: { pageSize: 200 },
    }),
    createPayloadBuilder: ({ value }, items) => ({
      size: String(value || "").trim(),
      sequence: items.length,
      model: modelId,
    }),
  });

  const [editOpen, setEditOpen] = React.useState(false);
  const [editSaving, setEditSaving] = React.useState(false);
  const [pendingEdit, setPendingEdit] = React.useState(null);

  React.useEffect(() => {
    colors.fetchItems();
    capacities.fetchItems();
  }, [modelId]); // eslint-disable-line

  const askEditColor = (it) => {
    setPendingEdit({
      type: "color",
      item: it,
      title: "แก้ไขสี",
      label: "ชื่อสี",
      value: it.name || "",
    });
    setEditOpen(true);
  };

  const askEditCapacity = (it) => {
    setPendingEdit({
      type: "capacity",
      item: it,
      title: "แก้ไขความจุ",
      label: "ความจุ",
      value: it.size || "",
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditOpen(false);
    setPendingEdit(null);
  };

  const confirmEdit = async (nextValue) => {
    const trimmed = String(nextValue ?? "").trim();
    if (!trimmed || !pendingEdit) return;

    try {
      setEditSaving(true);

      if (pendingEdit.type === "color") {
        const it = pendingEdit.item;
        if (trimmed === String(it.name || "").trim()) {
          setEditOpen(false);
          setPendingEdit(null);
          return;
        }
        await colors.http.put(`/colors/${it.id}`, {
          data: {
            name: trimmed,
            sequence: it.sequence ?? 0,
            model: modelId,
          },
        });
        await colors.fetchItems();
      }

      if (pendingEdit.type === "capacity") {
        const it = pendingEdit.item;
        const currentSize = String(it.size || "").trim();
        const duplicateSize = capacities.items.some(
          (cap) =>
            String(cap?.id) !== String(it.id) &&
            String(cap?.size || "").trim().toLowerCase() === trimmed.toLowerCase()
        );

        if (trimmed === currentSize) {
          setEditOpen(false);
          setPendingEdit(null);
          return;
        }

        if (duplicateSize) {
          alert("มีความจุนี้อยู่แล้ว");
          setEditSaving(false);
          return;
        }

        await capacities.http.put(`/capacities/${it.id}`, {
          data: {
            size: trimmed,
            sequence: it.sequence ?? 0,
          },
        });
        await capacities.fetchItems();
      }

      setEditOpen(false);
      setPendingEdit(null);
    } catch (err) {
      console.error("PUT child resource error:", err);
      alert("แก้ไขข้อมูลไม่สำเร็จ");
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <>
      <EditModal
        open={editOpen}
        title={pendingEdit?.title || "แก้ไขรายการ"}
        label={pendingEdit?.label || "ชื่อรายการ"}
        value={pendingEdit?.value || ""}
        confirmText="บันทึก"
        cancelText="ยกเลิก"
        loading={editSaving}
        onClose={closeEdit}
        onConfirm={confirmEdit}
      />

      <div className="ml-12 rounded-2xl border border-slate-200 bg-white p-4 space-y-5">
        <div>
          <div className="text-lg font-extrabold text-slate-900 mb-3">สี</div>
          <div className="space-y-3">
            <InlineCreateRow
              placeholder="เพิ่มสี"
              loading={colors.creating}
              onSubmit={colors.createItem}
            />

            {colors.loading ? <div className="text-sm text-slate-500">กำลังโหลด...</div> : null}

            {!colors.loading && colors.items.length === 0 ? (
              <div className="text-sm text-slate-500">ยังไม่มีรายการสี</div>
            ) : null}

            <div className="space-y-2">
              {colors.items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3"
                >
                  <div className="font-semibold text-slate-900">{it.name || "-"}</div>

                  <div className="flex items-center gap-2">
                    <ActionButton onClick={() => askEditColor(it)}>
                      <Pen width={18} height={18} />
                    </ActionButton>

                    <ActionButton
                      danger
                      onClick={() => colors.deleteItem(it)}
                      disabled={colors.deletingId === it.id}
                    >
                      <Trash2 width={18} height={18} />
                    </ActionButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="text-lg font-extrabold text-slate-900 mb-3">ความจุ</div>
          <div className="space-y-3">
            <InlineCreateRow
              placeholder="เพิ่มความจุ เช่น 128 GB"
              loading={capacities.creating}
              onSubmit={capacities.createItem}
            />

            {capacities.loading ? <div className="text-sm text-slate-500">กำลังโหลด...</div> : null}

            {!capacities.loading && capacities.items.length === 0 ? (
              <div className="text-sm text-slate-500">ยังไม่มีรายการความจุ</div>
            ) : null}

            <div className="space-y-2">
              {capacities.items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3"
                >
                  <div className="font-semibold text-slate-900">{it.size || "-"}</div>

                  <div className="flex items-center gap-2">
                    <ActionButton onClick={() => askEditCapacity(it)}>
                      <Pen width={18} height={18} />
                    </ActionButton>

                    <ActionButton
                      danger
                      onClick={() => capacities.deleteItem(it)}
                      disabled={capacities.deletingId === it.id}
                    >
                      <Trash2 width={18} height={18} />
                    </ActionButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function CategoryModelsPage() {
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const parent = location.state?.parent;
  const http = React.useMemo(() => createHttpClient(), []);

  const models = useResource({
    endpoint: "/models",
    paramsBuilder: () => ({
      filters: { category: { id: { $eq: categoryId } } },
      sort: ["sequence:asc", "id:asc"],
      pagination: { pageSize: 200 },
    }),
    createPayloadBuilder: ({ value }, items) => ({
      name: value,
      sequence: items.length,
      category: categoryId,
    }),
  });

  const [expandedModelId, setExpandedModelId] = React.useState(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editSaving, setEditSaving] = React.useState(false);
  const [pendingEdit, setPendingEdit] = React.useState(null);
  const [templateOpen, setTemplateOpen] = React.useState(false);
  const [templateSaving, setTemplateSaving] = React.useState(false);
  const [pendingTemplateModel, setPendingTemplateModel] = React.useState(null);

  React.useEffect(() => {
    models.fetchItems();
  }, [categoryId]); // eslint-disable-line

  const askEditModel = (it) => {
    setPendingEdit({
      item: it,
      title: "แก้ไขชื่อรุ่น",
      label: "ชื่อรุ่น",
      value: it.name || "",
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditOpen(false);
    setPendingEdit(null);
  };

  const confirmEdit = async (nextValue) => {
    const trimmed = String(nextValue ?? "").trim();
    if (!trimmed || !pendingEdit) return;

    try {
      setEditSaving(true);

      const it = pendingEdit.item;
      await http.put(`/models/${it.id}`, {
        data: {
          name: trimmed,
          sequence: it.sequence ?? 0,
          category: categoryId,
        },
      });

      await models.fetchItems();
      setEditOpen(false);
      setPendingEdit(null);
    } catch (err) {
      console.error(`PUT /models/${pendingEdit?.item?.id} error:`, err);
      alert("แก้ไขรุ่นไม่สำเร็จ");
    } finally {
      setEditSaving(false);
    }
  };

  const askEditTemplate = (it) => {
    setPendingTemplateModel({
      id: it.id,
      name: it.name || "",
      template: String(it.template || ""),
    });
    setTemplateOpen(true);
  };

  const closeTemplate = () => {
    if (templateSaving) return;
    setTemplateOpen(false);
    setPendingTemplateModel(null);
  };

  const confirmTemplate = async (nextValue) => {
    if (!pendingTemplateModel?.id) return;
    try {
      setTemplateSaving(true);
      await http.put(`/models/${pendingTemplateModel.id}`, {
        data: {
          template: String(nextValue ?? ""),
        },
      });
      await models.fetchItems();
      setTemplateOpen(false);
      setPendingTemplateModel(null);
    } catch (err) {
      console.error(`PUT /models/${pendingTemplateModel?.id} template error:`, err);
      alert("บันทึกเทมเพลตไม่สำเร็จ");
    } finally {
      setTemplateSaving(false);
    }
  };

  return (
    <div className="p-5 md:p-8 min-h-full bg-[#F5F5F5]">
      <EditModal
        open={editOpen}
        title={pendingEdit?.title || "แก้ไขรายการ"}
        label={pendingEdit?.label || "ชื่อรายการ"}
        value={pendingEdit?.value || ""}
        confirmText="บันทึก"
        cancelText="ยกเลิก"
        loading={editSaving}
        onClose={closeEdit}
        onConfirm={confirmEdit}
      />
      <TemplateModal
        open={templateOpen}
        title={pendingTemplateModel?.name ? `เทมเพลตคำอธิบายสินค้า: ${pendingTemplateModel.name}` : "เทมเพลตคำอธิบายสินค้า"}
        value={pendingTemplateModel?.template || ""}
        loading={templateSaving}
        onClose={closeTemplate}
        onSave={confirmTemplate}
      />

      <div className="text-sm text-slate-500 flex items-center gap-2">
        <span className="hover:underline cursor-pointer" onClick={() => navigate(-1)}>
          ตั้งค่า
        </span>
        <span className="text-slate-300">/</span>
        <span className="font-semibold text-[#047DED]">
          {parent?.name ? `รุ่นของ ${parent.name}` : "รุ่นสินค้า"}
        </span>
      </div>

      <div className="mt-1 text-4xl font-extrabold tracking-tight">
        Master Data
      </div>

      <div className="mt-6 space-y-4 max-w-5xl">
        <button
          type="button"
          onClick={models.createItem.bind(null, { value: "รุ่นใหม่" })}
          className="inline-flex items-center gap-2 rounded-xl bg-[#111111] px-10 py-4 text-sm font-bold text-white shadow-md hover:opacity-95 active:scale-[0.99]"
        >
          <PlusCircle width={18} height={18} />
          เพิ่มรุ่น
        </button>

        {models.loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-slate-500">
            กำลังโหลดข้อมูล...
          </div>
        ) : null}

        {!models.loading &&
          models.items.map((it, index) => {
            const isExpanded = expandedModelId === String(it.id);

            return (
              <div key={it.id} className="space-y-2">
                <div className="relative flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-4 shadow-sm border border-slate-200">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                      <GripVertical className="text-slate-500" />
                    </div>

                    <div className="font-semibold truncate select-none">
                      {it.name || `รุ่น ${index + 1}`}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => askEditTemplate(it)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-4 text-sm font-bold text-slate-800 shadow-sm hover:bg-slate-50 active:scale-[0.99]"
                    >
                      <FileText size={18} />
                      แก้ไข Template
                    </button>
                    <ActionButton onClick={() => askEditModel(it)}>
                      <Pen width={18} height={18} />
                    </ActionButton>

                    <ActionButton
                      danger
                      onClick={() => models.deleteItem(it)}
                      disabled={models.deletingId === it.id}
                    >
                      <Trash2 width={18} height={18} />
                    </ActionButton>

                    <ActionButton
                      onClick={() =>
                        setExpandedModelId((prev) =>
                          prev === String(it.id) ? null : String(it.id)
                        )
                      }
                    >
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </ActionButton>
                  </div>
                </div>

                {isExpanded ? <ModelChildren modelId={it.id} /> : null}
              </div>
            );
          })}

        {!models.loading && models.items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-slate-500">
            ยังไม่มีรุ่น
          </div>
        ) : null}
      </div>
    </div>
  );
}